import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  NotebookPen,
  FileText,
  Table2,
  GaugeCircle,
  Sun,
  Moon,
  LogOut,
  Search,
  Settings as SettingsIcon,
  BarChart3,
  User as UserIcon,
  Video,
  Beaker,
  Images,
  Rocket,
  ListTodo,
  ListChecks,
  Mail,
  CalendarClock,
  Menu,
  X,
  FolderKanban,
  ClipboardList,
} from "lucide-react";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { useAuth } from "../auth";
import { useT } from "../i18n";
import { useStudioZiele } from "../studioAccess";
import { useTheme } from "../ui/theme";
import { useMailUnread, UnreadPill } from "../ui/mailUnread";
import { shortcutHint } from "../mail/shortcuts";
import { CommandPalette } from "./CommandPalette";
import { QuickCapture } from "./QuickCapture";
import ProjectsSidebarTree from "./ProjectsSidebarTree";
import { NextMeetingChip } from "./NextMeetingChip";

function NavItem({
  to,
  icon,
  label,
  badge,
  accentBadge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number | string;
  /** Dezenter Ungelesen-Akzent-Pill (P1.7) — bei 0/undef unsichtbar. Getrennt
   *  vom bestehenden grauen `badge` (Zahl-Text), damit dessen Nutzung unberührt bleibt. */
  accentBadge?: number;
}) {
  const location = useLocation();
  const path = to.split("?")[0];
  const onPath = location.pathname === path || location.pathname.startsWith(path + "/");
  const isActive = to === "/" ? location.pathname === "/" : onPath;
  return (
    <NavLink
      to={to}
      // KORREKTUR 2026-07-29: die frühere Begründung hier („ohne `end` matcht
      // react-router / auf JEDEN Pfad") war falsch und ungeprüft. NavLink in
      // react-router-dom 6.30.4 fängt genau diesen Fall selbst ab
      // (node_modules/react-router-dom/dist/index.js:843-844): für to="/" ist
      // `endSlashPosition` = 1, und `locationPathname.charAt(1) === "/"` ist auf
      // „/mail" das „m" — also nicht aktiv. Nachgemessen: ohne `end` bleibt
      // aria-current auf allen Hauptseiten genau einmal gesetzt.
      // `end` bleibt trotzdem stehen: es sagt die Absicht („nur exakt /")
      // ausdrücklich, statt sie an eine interne Schutzabfrage der Bibliothek zu
      // hängen. Es ist Absicherung, nicht Bugfix.
      end={to === "/"}
      aria-label={label}
      data-nav-to={to}
      data-nav-active={isActive ? "1" : "0"}
      className={clsx(
        "relative flex items-center rounded-md text-sm group transition-colors",
        "gap-2.5 px-2.5 min-h-11 md:min-h-0 md:py-1.5",
        isActive
          ? "bg-brand-50 dark:bg-ink-700 text-brand-700 font-medium"
          : "text-ink-50 hover:bg-cream-100 dark:hover:bg-ink-700/60 hover:text-ink"
      )}
    >
      {isActive && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-brand-500" />
      )}
      <span
        className={clsx(
          "relative transition-colors",
          isActive ? "text-brand-500" : "text-ink-50/60 group-hover:text-current"
        )}
      >
        {icon}
      </span>
      <span className="truncate flex-1">{label}</span>
      {accentBadge !== undefined && accentBadge > 0 && <UnreadPill count={accentBadge} />}
      {badge !== undefined && (
        <span className="text-xs tabular-nums text-ink-50/60">{badge}</span>
      )}
    </NavLink>
  );
}

/** Schriftzug im Kopf der Leiste. */
function Wortmarke() {
  return (
    <Link to="/" className="flex items-baseline gap-1.5">
      <span className="font-display text-xl font-semibold text-ink tracking-tight">
        bitop
      </span>
      <span className="font-display text-xl italic text-brand-500">Atlas</span>
    </Link>
  );
}

/** Gruppen-Überschrift der Desktop-Navigation. */
function NavGroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-3 mb-1 px-2 text-xs uppercase tracking-wider text-ink-50/50">
      {children}
    </div>
  );
}

/** Mobile-only Nav-Kachel (App-Launcher-Stil, PWA). Icon in accent-soft-Kachel
 *  + Label, KEIN Beschreibungstext. Card-Optik (bg-surface + Border + rounded-xl). */
function NavTile({
  to,
  icon,
  label,
  accentBadge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  /** Dezenter Ungelesen-Akzent-Pill (P1.7) in der oberen rechten Ecke. */
  accentBadge?: number;
}) {
  const location = useLocation();
  const path = to.split("?")[0];
  const isActive =
    to === "/"
      ? location.pathname === "/"
      : location.pathname === path || location.pathname.startsWith(path + "/");
  return (
    <NavLink
      to={to}
      // Eigener Name, NICHT `data-nav-to`: die Walk-Zusagen zählen über
      // `[data-nav-panel] [data-nav-to]`, und beide Navigationen (Mobil-Kacheln
      // und Desktop-Liste) stehen gleichzeitig im DOM. Ein gemeinsamer Marker
      // hätte dort die doppelte Zahl ergeben. Hiermit ist die Kachel-Navigation
      // für sich zählbar.
      data-nav-tile={to}
      className={clsx(
        "relative flex flex-col items-center justify-center gap-2 py-5 rounded-xl border bg-surface transition-colors",
        isActive
          ? "border-accent-300 bg-accent-50/60"
          : "border-border hover:border-accent-300 active:bg-surface-2"
      )}
    >
      {accentBadge !== undefined && accentBadge > 0 && (
        <UnreadPill count={accentBadge} className="absolute top-2 right-2" />
      )}
      <span
        className={clsx(
          "w-11 h-11 rounded-xl grid place-items-center",
          isActive ? "bg-accent-100 text-accent-600" : "bg-accent-50 text-accent-600"
        )}
      >
        {icon}
      </span>
      <span className="text-sm font-medium text-text">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { username, logout } = useAuth();
  /** Beschriftungen der Navigation. Kachel (<md) und Zeile (>=md) lesen
   *  denselben Key — bis auf die zwei Ziele, deren Kachel im Bestand kürzer
   *  beschriftet ist (nav.ts). */
  const t = useT();
  /** Sichtbarkeit der Studio-Ziele (#81, seit Mig 0099 je Ziel einzeln) —
   *  Menü-Kosmetik, KEINE Zugriffsgrenze. Regel + Begründung stehen an EINER
   *  Stelle: `studioAccess.ts`. */
  const studio = useStudioZiele();
  const { unreadCount: mailUnread } = useMailUnread();
  const { effective, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  // Der Microsoft-365-Kalender (/calendar), NICHT der Aufgaben-Kalender
  // (/tasks?view=kalender): nur dort steht das Raster, das die Sidebar-Liste
  // doppeln wuerde.
  const istKalenderSeite =
    location.pathname === "/calendar" ||
    location.pathname.startsWith("/calendar/");

  // Scroll-Lock des ECHTEN Scrollers (<main>, nicht body — der Root ist
  // `overflow-hidden`) solange der Mobile-Drawer offen ist. Ohne Lock leaken
  // Touch-Scrolls über dem full-screen fixed-Drawer auf <main> durch und
  // verschieben dessen scrollTop; beim Schliessen „rutscht" die Seite dann auf
  // eine andere Position. `overflow:hidden` friert scrollTop ein (kein Reflow,
  // kein Sprung) und blockt das Durchscrollen. Nur <md relevant — auf Desktop
  // ist der Drawer statisch, sidebarOpen bleibt false. Zusätzlicher Width-Guard
  // stellt sicher, dass das Desktop-Layout garantiert unangetastet bleibt.
  useEffect(() => {
    const main = mainRef.current;
    if (!main || !sidebarOpen || window.innerWidth >= 768) return;
    const prevOverflow = main.style.overflow;
    main.style.overflow = "hidden";
    return () => {
      main.style.overflow = prevOverflow;
    };
  }, [sidebarOpen]);

  // Sidebar bei Navigation automatisch schliessen (Mobile-Drawer-Pattern).
  // Auf >=md ist die Sidebar dauerhaft sichtbar, da spielt sidebarOpen keine Rolle.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // PWA-Gesten (Mobile, <md): Edge-Swipe öffnet, Links-Swipe schliesst den
  // Vollbild-Drawer. Reine Touch-Events, keine Dependency. Trigger-basiert
  // (Schwelle 60px) + die vorhandene translateX-Transition der <aside>.
  // Vertikales Scrollen wird NICHT gekapert: horizontale Dominanz nur wenn
  // |dx| > |dy| * 1.5.
  useEffect(() => {
    const EDGE = 24; // linker Rand-Streifen zum Aufziehen
    const THRESHOLD = 60; // Mindest-Wischweg
    let startX = 0;
    let startY = 0;
    let mode: "open" | "close" | null = null;

    function onStart(e: TouchEvent) {
      if (e.touches.length !== 1) {
        mode = null;
        return;
      }
      if (window.innerWidth >= 768) {
        mode = null; // Desktop-Sidebar ist statisch — keine Gesten
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      if (!sidebarOpen && startX <= EDGE) mode = "open";
      else if (sidebarOpen) mode = "close";
      else mode = null;
    }

    function onMove(e: TouchEvent) {
      if (!mode) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) <= Math.abs(dy) * 1.5) return; // vertikales Scrollen gewinnt
      if (mode === "open" && dx >= THRESHOLD) {
        setSidebarOpen(true);
        mode = null;
      } else if (mode === "close" && dx <= -THRESHOLD) {
        setSidebarOpen(false);
        mode = null;
      }
    }

    function onEnd() {
      mode = null;
    }

    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [sidebarOpen]);

  // Browser-Back schliesst den offenen Drawer statt zu navigieren (PWA-Verhalten):
  // beim Öffnen einen History-Eintrag pushen, popstate fängt „Zurück" ab. Schliesst
  // der Drawer anders (X / Backdrop / Nav), räumen wir den Dummy wieder ab.
  useEffect(() => {
    if (!sidebarOpen) return;
    window.history.pushState({ sbDrawer: true }, "");
    const onPop = () => setSidebarOpen(false);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Nur aufräumen, wenn unser Dummy noch obenauf liegt (Schliessen ohne
      // Zurück). Bei Zurück ist er bereits gepoppt; bei Nav liegt die Route
      // darüber — dann kein doppeltes back().
      if (window.history.state?.sbDrawer) window.history.back();
    };
  }, [sidebarOpen]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      const inField =
        t?.tagName === "INPUT" ||
        t?.tagName === "TEXTAREA" ||
        // contentEditable (z. B. der P1.10-Rich-Text-Composer) ist ebenso ein
        // Eingabefeld — Einzeltasten-Kürzel (z. B. „n") dürfen dort NICHT feuern.
        !!t?.isContentEditable;
      // Befehls-Palette (Doc 04 / X1): `Alt+Q` (Office „Was möchten Sie tun?")
      // ODER überall/im Verfassen-Editor `Strg+Umschalt+P`. Das früher hier
      // gebundene `Strg/Cmd+K` ist ENTFERNT — die Doktrin verbietet ⌘K als
      // Palette (Strg+K bleibt frei für Editor-Hyperlink, Mail.tsx:2173, und für
      // die Browser-Adressleiste). Beide preventDefault: Alt+Q fokussiert sonst
      // das Browser-Menü, Strg+Umschalt+P ist sonst z. T. browserbelegt.
      if (
        (e.altKey && !e.ctrlKey && !e.metaKey && e.key.toLowerCase() === "q") ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "p")
      ) {
        e.preventDefault();
        setPaletteOpen(true);
      } else if (!inField && e.key.toLowerCase() === "n" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setQuickOpen(true);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
        setQuickOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile backdrop — schliesst den Drawer per Klick. md:hidden, weil auf
          Desktop die Sidebar permanent sichtbar ist und kein Overlay braucht. */}
      {sidebarOpen && (
        <button
          type="button"
          aria-label={t.shared.rahmen.sidebarSchliessen}
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — auf mobile fixed Off-Canvas-Drawer (Vollbild), auf >=md die
          dauerhaft offene 240px-Spalte. Sie ist NICHT einklappbar: die
          Symbol-Schiene ist mit #78 zurückgenommen (2026-07-31). Wer sie wieder
          will, baut sie neu — Halb-Reste im Markup wären eine Falle. */}
      <aside
        id="primary-sidebar"
        className={clsx(
          "fixed inset-y-0 left-0 z-50 w-full overscroll-contain",
          "transform transition-transform duration-200 ease-out",
          "md:relative md:inset-auto md:translate-x-0 md:max-w-none md:shrink-0 md:w-60",
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
      <div
        data-nav-panel
        className="flex h-full flex-col border-r border-cream-200 dark:border-ink-600 bg-cream-100 dark:bg-ink-800"
      >
        {/* Kopf: Schriftzug links, Lupe (+ mobil das Schliessen-Kreuz) rechts. */}
        <div className="border-b border-cream-200 dark:border-ink-600 flex items-center md:h-[60px] px-4 py-4 gap-2 justify-between">
          <Wortmarke />
          <div className="flex items-center gap-1">
            {/* Kompakte Lupe — öffnet die CommandPalette (ersetzt die alte
                „Suchen oder ausführen"-Box). Kürzel = Alt+Q (Doc 04, aus
                shortcuts.ts — kein hartkodiertes ⌘K mehr). */}
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              aria-label={t.shared.palette.oeffnen}
              title={t.shared.palette.knopfTitel(shortcutHint("palette"))}
              className="w-8 h-8 grid place-items-center rounded-md text-ink-50/60 hover:bg-cream-200/60 dark:hover:bg-ink-700 hover:text-ink transition-colors"
            >
              <Search className="w-4 h-4" />
            </button>
            {/* Expliziter Close-Button im Drawer-Header — Mobile-only. */}
            <button
              type="button"
              aria-label={t.shared.rahmen.sidebarSchliessen}
              onClick={() => setSidebarOpen(false)}
              className="md:hidden w-11 h-11 -mr-2 grid place-items-center rounded-md text-ink-50/70 hover:bg-cream-200/60 dark:hover:bg-ink-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* X2: Terminliste der nächsten 7 Tage (Desktop), tagesweise gruppiert,
            ~4-5 Zeilen sichtbar, der Rest scrollbar. Eigener Block im
            Sidebar-Kopf; ohne Termine bleibt eine ruhige Zeile stehen.
            E2 (Audit-Befund 8): AUSSER auf /calendar. Dort steht dieselbe
            Terminliste als Raster daneben — der Block waere ein Duplikat, das
            der Nutzer zweimal lesen muss und das den Kalender schmaler macht.
            Bewusst nur diese eine Route: auf allen anderen Seiten ist die
            Liste der einzige Blick auf den Tag und bleibt unveraendert. */}
        {!istKalenderSeite && <NextMeetingChip variant="desktop" />}

        {/* MOBILE (<md): App-Launcher-Kacheln (2 Spalten) + Projekt-Tree.
            Genau EINE Navigation auf Mobile. */}
        <nav className="md:hidden flex-1 overflow-y-auto p-3 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <NavTile to="/" icon={<GaugeCircle className="w-6 h-6" />} label={t.nav.uebersicht} />
            <NavTile to="/tasks" icon={<ListTodo className="w-6 h-6" />} label={t.nav.aufgaben} />
            <NavTile to="/mail" icon={<Mail className="w-6 h-6" />} label={t.nav.mail} accentBadge={mailUnread} />
            <NavTile to="/calendar" icon={<CalendarClock className="w-6 h-6" />} label={t.nav.kalender} />
            <NavTile to="/n" icon={<NotebookPen className="w-6 h-6" />} label={t.nav.notizen} />
            <NavTile to="/docs" icon={<FileText className="w-6 h-6" />} label={t.nav.dokumente} />
            <NavTile to="/ds" icon={<Table2 className="w-6 h-6" />} label={t.nav.tabellen} />
            {/* Studio-Gruppe (#81) — dieselben fünf Einträge und dieselbe
                Reihenfolge wie in der Desktop-Navigation, und dieselben
                Sichtbarkeits-Regeln. Ohne die Gates hier sähe genau derselbe
                Nutzer am Telefon, was ihm am Rechner ausgeblendet wird. */}
            {studio["/email-automation"] && (
              <NavTile
                to="/email-automation"
                icon={<ListChecks className="w-6 h-6" />}
                label={t.nav.emailAutomatisierungenKachel}
              />
            )}
            {studio["/claims"] && (
              <NavTile to="/claims" icon={<Beaker className="w-6 h-6" />} label={t.nav.claimFreigaben} />
            )}
            {studio["/library"] && (
              <NavTile to="/library" icon={<Images className="w-6 h-6" />} label={t.nav.bildBibliothek} />
            )}
            {studio["/heygen"] && (
              <NavTile to="/heygen" icon={<Video className="w-6 h-6" />} label={t.nav.videoStudioKachel} />
            )}
            {studio["/blog"] && (
              <NavTile to="/blog/dashboard" icon={<Rocket className="w-6 h-6" />} label={t.nav.blog} />
            )}
            {studio["/fragebogen"] && (
              <NavTile
                to="/fragebogen"
                icon={<ClipboardList className="w-6 h-6" />}
                label={t.nav.fragebogen}
              />
            )}
          </div>

          <div>
            <div className="mb-1.5 px-1 text-xs uppercase tracking-wider text-ink-50/50">
              {t.nav.gruppeProjekte}
            </div>
            <ProjectsSidebarTree />
          </div>
        </nav>

        {/* DESKTOP (>=md): die Listen-Sidebar. Dauerhaft offen — es gibt keinen
            eingeklappten Zustand mehr (#78). */}
        <nav className="hidden md:block flex-1 overflow-y-auto space-y-0.5 p-2">
          <NavItem to="/" icon={<GaugeCircle className="w-4 h-4" />} label={t.nav.uebersicht} />

          <NavGroupLabel>{t.nav.gruppeAlltag}</NavGroupLabel>
          <NavItem to="/tasks" icon={<ListTodo className="w-4 h-4" />} label={t.nav.aufgaben} />
          <NavItem
            to="/mail"
            icon={<Mail className="w-4 h-4" />}
            label={t.nav.mail}
            accentBadge={mailUnread}
          />
          <NavItem
            to="/calendar"
            icon={<CalendarClock className="w-4 h-4" />}
            label={t.nav.kalender}
          />
          <NavItem to="/n" icon={<NotebookPen className="w-4 h-4" />} label={t.nav.notizen} />
          <NavItem to="/docs" icon={<FileText className="w-4 h-4" />} label={t.nav.dokumente} />
          <NavItem to="/ds" icon={<Table2 className="w-4 h-4" />} label={t.nav.tabellen} />

          <NavGroupLabel>{t.nav.gruppeProjekte}</NavGroupLabel>
          {/* „Projekte" ist ein FESTES Ziel und steht deshalb über dem Baum:
              der Baum listet die einzelnen Projekte, der Eintrag führt auf die
              Projekt-Übersicht. */}
          <NavItem to="/p" icon={<FolderKanban className="w-4 h-4" />} label={t.nav.projekte} />
          <ProjectsSidebarTree />

          {/* Studio-Gruppe (#81): „Email-Automatisierungen" ist aus „Alltag"
              hierher gewandert. Seit Mig 0099 hat JEDER Eintrag sein eigenes
              Gate — wer nur Video freigeschaltet hat, sieht hier auch nur Video.
              Die Überschrift hängt an `irgendeins` — sonst bliebe für alle
              anderen eine leere Gruppe „Studio" stehen. */}
          {studio.irgendeins && <NavGroupLabel>{t.nav.gruppeStudio}</NavGroupLabel>}
          {studio["/email-automation"] && (
            <NavItem
              to="/email-automation"
              icon={<ListChecks className="w-4 h-4" />}
              label={t.nav.emailAutomatisierungen}
            />
          )}
          {studio["/claims"] && (
            <NavItem
              to="/claims"
              icon={<Beaker className="w-4 h-4" />}
              label={t.nav.claimFreigaben}
            />
          )}
          {studio["/library"] && (
            <NavItem
              to="/library"
              icon={<Images className="w-4 h-4" />}
              label={t.nav.bildBibliothek}
            />
          )}
          {studio["/heygen"] && (
            <NavItem to="/heygen" icon={<Video className="w-4 h-4" />} label={t.nav.videoStudio} />
          )}
          {studio["/blog"] && (
            <NavItem to="/blog/dashboard" icon={<Rocket className="w-4 h-4" />} label={t.nav.blog} />
          )}
          {studio["/fragebogen"] && (
            <NavItem
              to="/fragebogen"
              icon={<ClipboardList className="w-4 h-4" />}
              label={t.nav.fragebogen}
            />
          )}
        </nav>

        {/* Fuss: Profil-Menü, Hell/Dunkel. */}
        <div className="border-t border-cream-200 dark:border-ink-600 flex items-center gap-2 p-2">
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <button
                className="flex items-center rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 gap-2 min-w-0 py-1 flex-1"
                title={t.shared.rahmen.profilMenue}
              >
                <span className="w-7 h-7 rounded-full bg-brand-500 grid place-items-center text-white text-xs font-bold shrink-0 shadow-sm shadow-brand-500/30">
                  {(username ?? "?").charAt(0).toUpperCase()}
                </span>
                <span className="text-sm flex-1 truncate text-left">{username ?? "—"}</span>
              </button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content
                side="top"
                align="start"
                sideOffset={6}
                className="z-50 min-w-[200px] bg-cream-50 dark:bg-ink-800 border border-cream-200 dark:border-ink-600 rounded-lg shadow-lg py-1 animate-fade-in"
              >
                <div className="px-3 py-2 text-xs text-ink-50/70 border-b border-cream-200 dark:border-ink-600">
                  <div className="flex items-center gap-1.5">
                    <UserIcon className="w-3.5 h-3.5" />
                    <span className="truncate">{username ?? "—"}</span>
                  </div>
                </div>
                <Dropdown.Item asChild>
                  <Link
                    to="/stats"
                    className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream-100 dark:hover:bg-ink-700 outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <BarChart3 className="w-4 h-4 text-ink-50/60" />
                    <span>{t.shared.rahmen.statistik}</span>
                  </Link>
                </Dropdown.Item>
                <Dropdown.Item asChild>
                  <Link
                    to="/cfg"
                    className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream-100 dark:hover:bg-ink-700 outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <SettingsIcon className="w-4 h-4 text-ink-50/60" />
                    <span>{t.shared.rahmen.einstellungen}</span>
                  </Link>
                </Dropdown.Item>
                <Dropdown.Separator className="h-px bg-cream-200 dark:bg-ink-600 my-1" />
                <Dropdown.Item
                  onSelect={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-cream-100 dark:hover:bg-ink-700 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 cursor-pointer text-danger"
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t.shared.rahmen.abmelden}</span>
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
          <button
            onClick={toggle}
            className="rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 text-ink-50/60 p-1"
            title={effective === "dark" ? t.shared.rahmen.designHell : t.shared.rahmen.designDunkel}
            aria-label={
              effective === "dark"
                ? t.shared.rahmen.designHellAria
                : t.shared.rahmen.designDunkelAria
            }
          >
            {effective === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>
      </div>
      </aside>

      {/* Content-Spalte (Top-Bar nur mobile + Main) */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile-only Top-Bar mit Burger + Logo. md:hidden weil Desktop die
            permanente Sidebar bereits Branding + Suche traegt. */}
        <header
          className="md:hidden shrink-0 border-b border-cream-200 dark:border-ink-600 bg-cream-100 dark:bg-ink-800 flex items-center px-2 gap-2"
          style={{
            paddingTop: "max(env(safe-area-inset-top), 0px)",
            paddingLeft: "max(env(safe-area-inset-left), 0.5rem)",
            paddingRight: "max(env(safe-area-inset-right), 0.5rem)",
            minHeight: "calc(3.5rem + env(safe-area-inset-top))",
          }}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t.shared.rahmen.sidebarOeffnen}
            aria-expanded={sidebarOpen}
            aria-controls="primary-sidebar"
            className="w-11 h-11 grid place-items-center rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 text-ink"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="flex items-baseline gap-1.5">
            <span className="font-display text-lg font-semibold text-ink tracking-tight">
              bitop
            </span>
            <span className="font-display text-lg italic text-brand-500">Atlas</span>
          </Link>
          {/* X2: kompakter Termin-Chip (Icon + Countdown, ohne Titel) in der
              Mobile-Top-Bar, VOR der ml-auto-Lupe. Rendert nur, wenn ein Termin
              ansteht — sonst nichts (die Lupe rückt dann an den rechten Rand). */}
          <NextMeetingChip variant="mobile" />
          {/* Lupe rechts im Mobile-Header — öffnet die CommandPalette (Alt+Q). */}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label={t.shared.palette.oeffnen}
            title={t.shared.palette.knopfTitel(shortcutHint("palette"))}
            className="ml-auto w-11 h-11 grid place-items-center rounded-md hover:bg-cream-200/60 dark:hover:bg-ink-700 text-ink-50/70"
          >
            <Search className="w-5 h-5" />
          </button>
        </header>

        <main ref={mainRef} className="flex-1 overflow-auto bg-cream-50 dark:bg-ink-900">
          <Outlet />
        </main>
      </div>

      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          onQuickNote={() => {
            setPaletteOpen(false);
            setQuickOpen(true);
          }}
        />
      )}
      <QuickCapture open={quickOpen} onClose={() => setQuickOpen(false)} />
    </div>
  );
}
